import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { UrlSegment, UrlTree } from '@angular/router';
import { LinkDefinition } from '../configurations/seo';

@Injectable({
  providedIn: 'root',
})
export class LinkService {
  private readonly document: Document = inject(DOCUMENT);

  /**
   * Set Canonical Link to Head
   */
  setCanonicalLink(tree: UrlTree): void {
    const href: string = this.parseUrlTree(tree);
    this.updateTag({
      rel: 'canonical',
      href,
    });
  }

  setCanonicalLinkFromEndpoint(canonical: LinkDefinition): void {
    this.updateTag(canonical);
  }

  setAlternateLink(tree: UrlTree, hreflang: string): void {
    const href: string = this.parseUrlTree(tree, hreflang);
    this.updateTag({
      rel: 'alternate',
      href,
      hreflang
    }, true);
  }

  private parseUrlTree(tree: UrlTree, alternateLangSegment?: string): string {
    let url: string = '';
    if (tree.root.hasChildren()) {
      const segments: UrlSegment[] = tree.root.children.primary.segments;
      if (segments && segments.length > 0) {
        if (alternateLangSegment) {
          segments[0].path = alternateLangSegment;
        }
        url = segments.map(segment => segment.path).join('/');
        if (url.endsWith('.')) {
          url = url.slice(0, -1);
        }
      }
    }
    return url.length > 0 ? `${globalThis.location.origin}/${url}` : globalThis.location.origin;
  }

  /**
   * Create or update a link tag
   */
  public updateTag(tag: LinkDefinition, withLang?: boolean): void {
    let selector: string;
    if (withLang && tag.hreflang !== '') {
      selector = this._parseSelector(tag, tag.hreflang);
    } else {
      selector = this._parseSelector(tag);
    }
    const linkElement: HTMLLinkElement = this.document.head.querySelector(selector) as HTMLLinkElement
      || this.document.head.appendChild(this.document.createElement('link'));
    if (linkElement) {
      Object.keys(tag).forEach((prop: string) => {
        linkElement.setAttribute(prop, tag[prop]);
      });
    }
  }

  /**
   * Remove a link tag from DOM
   */
  public removeTag(tag: LinkDefinition): void {
    const selector: string = this._parseSelector(tag);
    const linkElement: HTMLLinkElement = this.document.head.querySelector(selector) as HTMLLinkElement;
    if (linkElement) {
      linkElement.remove();
    }
  }

  /**
   * Get link tag
   */
  public getTag(tag: LinkDefinition): HTMLLinkElement | null {
    const selector: string = this._parseSelector(tag);
    return this.document.head.querySelector(selector);
  }

  /**
   * Get all link tags
   */
  public getTags(): NodeListOf<HTMLLinkElement> {
    return this.document.head.querySelectorAll('link');
  }

  /**
   * Parse tag to create a selector
   */
  private _parseSelector(tag: LinkDefinition, withLang?: string): string {
    const attr: string = tag.rel ? 'rel' : 'hreflang';
    if (withLang) {
      return `link[${attr}="${tag[attr]}"][hreflang="${withLang}"]`;
    }
    return `link[${attr}="${tag[attr]}"]`;
  }
}
